import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { useAuth } from '../hooks/useAuth';
import { api } from '../utils/api';
import { LicenseInformationModal } from './LicenseInformationModal';

interface HeaderProps {
  title?: string;
}

/**
 * Header component with server time display and user menu
 * Displays logo on mobile, server time, and user profile dropdown
 */
export const Header: React.FC<HeaderProps> = ({ title: _title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [serverTime, setServerTime] = useState<Date | null>(null);
  const [timeDiff, setTimeDiff] = useState<number>(0);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch server time on mount
  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        const data = await api.getServerTime();
        const serverDate = new Date(data.timestamp);
        const clientDate = new Date();
        
        // Calculate time difference between server and client
        setTimeDiff(serverDate.getTime() - clientDate.getTime());
        setServerTime(serverDate);
      } catch (error) {
        console.error('Error fetching server time:', error);
        // Fallback to client time if server time fetch fails
        setServerTime(new Date());
        setTimeDiff(0);
      }
    };

    fetchServerTime();
    // Refresh server time every 5 minutes
    const interval = setInterval(fetchServerTime, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Update displayed time every second
  useEffect(() => {
    if (serverTime === null) return;

    const interval = setInterval(() => {
      // Use client time + time difference to simulate server time
      const currentServerTime = new Date(Date.now() + timeDiff);
      setServerTime(currentServerTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeDiff, serverTime]);

  // Handle user menu toggle
  const toggleUserMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Handle logout
  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  };

  // Handle settings navigation
  const handleSettings = () => {
    setIsUserMenuOpen(false);
    navigate('/shift-settings');
  };

  // Handle license information
  const handleLicenseInfo = () => {
    setIsUserMenuOpen(false);
    setIsLicenseModalOpen(true);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        userButtonRef.current &&
        !userMenuRef.current.contains(e.target as Node) &&
        !userButtonRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isUserMenuOpen) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isUserMenuOpen]);

  return (
    <header className="bg-white border-b border-gray-200 fixed top-0 right-0 left-0 md:left-64 h-16 z-10">
      <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6">
        {/* Logo - Only visible on mobile */}
        <div className="flex items-center md:hidden">
          <Logo size="lg" />
        </div>

        {/* Spacer for desktop (hidden logo area) */}
        {/* Server Time Display */}
          <div className="flex items-center hidden md:block space-x-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {/* <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 bg-green-800 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-800 font-semibold">Live</span>
                </div> */}
                <span className="text-lg text-gray-800 font-bold hidden sm:block">Dashboard Live Data</span>
              </div>
              <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-sm font-semibold text-blue-800">
                  {serverTime ? serverTime.toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : '--/--/----'}
                </span>
                <span className="text-sm font-semibold text-blue-800">
                  {serverTime ? serverTime.toLocaleTimeString('id-ID', {
                    hour: '2-digit',
                    minute: '2-digit',
                    // second: '2-digit',
                    hour12: false
                  }) : '--:--:--'}
                </span>
              </div>
            </div>
          </div>

        {/* User Controls */}
        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
          {/* Server Time Display */}
          <div className="flex items-center md:hidden block space-x-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {/* <div className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 bg-green-800 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-800 font-semibold">Live</span>
                </div> */}
                <span className="text-lg text-gray-800 font-bold hidden sm:block">Dashboard Live Data</span>
              </div>
              <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-sm font-semibold text-blue-800">
                  {serverTime ? serverTime.toLocaleDateString('id-ID', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : '--/--/----'}
                </span>
                <span className="text-sm font-semibold text-blue-800">
                  {serverTime ? serverTime.toLocaleTimeString('id-ID', {
                    hour: '2-digit',
                    minute: '2-digit',
                    // second: '2-digit',
                    hour12: false
                  }) : '--:--:--'}
                </span>
              </div>
            </div>
          </div>
          
          {/* User Profile */}
          <div className="flex items-center space-x-2 ml-2">
            {/* User name - Hidden on small screens */}
            <div className="text-sm text-gray-600 hidden md:block">
              <span className="text-sm font-medium text-gray-700">
                {user?.nama || 'User'}
              </span>
            </div>
            
            {/* User avatar */}
            <div className="relative hidden md:block">
              <button
                ref={userButtonRef}
                onClick={toggleUserMenu}
                className="focus:outline-none"
                aria-haspopup="true"
                aria-expanded={isUserMenuOpen}
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 border-gray-200 hover:border-gray-600 transition-colors cursor-pointer bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold text-sm">
                    {user?.nama ? user.nama.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
              </button>

              {/* User dropdown menu */}
              {isUserMenuOpen && (
                <div
                  ref={userMenuRef}
                  className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-20"
                >
                  <button
                    onClick={handleLicenseInfo}
                    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100"
                  >
                    License Information
                  </button>
                  <button
                    onClick={handleSettings}
                    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100"
                  >
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left block px-4 py-2 text-sm text-red-700 hover:bg-red-100"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* License Information Modal */}
      <LicenseInformationModal 
        isOpen={isLicenseModalOpen} 
        onClose={() => setIsLicenseModalOpen(false)} 
      />
    </header>
  );
};
